import { load } from '@loaders.gl/core';
import { FlatGeobufLoader } from '@loaders.gl/flatgeobuf';
import { geojson as flatgeobufGeojson } from 'flatgeobuf';

/**
 * Load a map layer as GeoJSON. Paths ending in .json/.geojson are logical URLs
 * used by the app config; on disk we store FlatGeobuf (.fgb) only, so this
 * function fetches the .fgb sibling and decodes it in the browser. The download
 * button uses the same path to produce a GeoJSON file client-side.
 * @param {string} url logical layer URL (typically ends in .json)
 */
function asFeatureCollection(data) {
    if (!data) return data;
    if (data.type === 'FeatureCollection' && Array.isArray(data.features)) return data;
    if (Array.isArray(data)) {
        return { type: 'FeatureCollection', features: data };
    }
    return data;
}

export async function getJSON(url, { bbox = null } = {}) {
    console.log(`Fetching data: ${url}`);
    try {
        const isFgb = url.toLowerCase().endsWith('.fgb');
        // loaders.gl's FlatGeobuf web worker currently rejects the CRW
        // Pacific polygon encoding with `Unknown type 9`.  Use the native
        // FlatGeobuf spatial reader for this product instead; it performs
        // range reads and bypasses that worker entirely.  These tiles use
        // 0..360 longitude, hence the 93..245° Pacific data extent.
        const pacificCrw = /\/CRW-BAA-Pacific\/(?:1deg|2deg)\//.test(url);
        const requestedBbox = bbox || (pacificCrw ? [90, -50, 250, 60] : null);
        // GeoPandas writes clipped MHW cells as MultiPolygons.  The
        // loaders.gl worker mishandles their FlatGeobuf geometry encoding
        // ("Unknown type 9"), while the upstream FlatGeobuf GeoJSON reader
        // decodes the same files correctly. Keep MHW on that proven path.
        const marineHeatWaves = /\/jsonFiles\/(?:mhw|mhw-Pacific)\//.test(url);

        // EEZ outlines are small, stable GeoJSON files.  Do not substitute their
        // older FlatGeobuf siblings: loaders.gl currently returns an Arrow-shaped
        // object for those files rather than a GeoJSON FeatureCollection, which
        // causes deck.gl's "Unknown GeoJSON type" error.
        const keepJsonBoundary = /\/CMIPtas\/(?:palauEEZ|rmiEEZ)\.json$/i.test(url);

        // Intercept JSON/GeoJSON requests and try fetching the .fgb equivalent first
        if (!keepJsonBoundary && (url.toLowerCase().endsWith('.json') || url.toLowerCase().endsWith('.geojson'))) {
            const fgbUrl = url.replace(/\.(json|geojson)$/i, '.fgb');

            try {
                if (requestedBbox) {
                    // FlatGeobuf has an R-tree index. The native reader issues
                    // HTTP range requests for just the requested extent instead
                    // of downloading and decoding the entire basin product.
                    const features = [];
                    for await (const feature of flatgeobufGeojson.deserialize(fgbUrl, requestedBbox)) features.push(feature);
                    return { type: 'FeatureCollection', features };
                }
                const fgbResp = await fetch(fgbUrl);
                if (fgbResp.ok) {
                    // Prevent Create React App's fallback HTML from being parsed by the worker,
                    // which causes an Uncaught Worker Error overlay even if the promise is caught.
                    const contentType = fgbResp.headers.get('content-type');
                    if (contentType && contentType.includes('text/html')) {
                        throw new Error(`Received HTML instead of FlatGeobuf for ${fgbUrl}.`);
                    }

                    console.log(`Found FlatGeobuf version: ${fgbUrl}`);
                    if (marineHeatWaves) {
                        const bytes = new Uint8Array(await fgbResp.arrayBuffer());
                        const features = [];
                        for await (const feature of flatgeobufGeojson.deserialize(bytes)) features.push(feature);
                        return { type: 'FeatureCollection', features };
                    }
                    // Pass the active Response directly to avoid a double-download
                    const geojsonData = await load(fgbResp, FlatGeobufLoader);
                    return asFeatureCollection(geojsonData);
                }
            } catch (e) {
                // Ignore network fetch errors and gracefully fall back to the original URL
                console.log(`Falling back to original JSON/GeoJSON for ${url}`);
            }
        }

        // --- Fast-path for explicit FlatGeobuf requests ---
        if (isFgb) {
            // The native reader yields GeoJSON Features directly.  Regional
            // sea-level files include polygon and multipolygon cells, for which
            // the loaders.gl worker currently returns a non-GeoJSON table.
            // Decode these with the native reader so deck.gl receives the
            // FeatureCollection its GeoJsonLayer expects.
            if (url.includes('/regional-sea-level/') || marineHeatWaves) {
                const response = await fetch(url);
                if (!response.ok) throw new Error(`Unable to fetch FlatGeobuf (${response.status}) for ${url}`);
                const features = [];
                // URL input to the FlatGeobuf reader requires a spatial rect.
                // These national monthly frames are intentionally small, so
                // decoding the fetched bytes avoids an unnecessary range-query
                // requirement and works consistently in all browsers.
                const bytes = new Uint8Array(await response.arrayBuffer());
                for await (const feature of flatgeobufGeojson.deserialize(bytes)) features.push(feature);
                return { type: 'FeatureCollection', features };
            }
            if (requestedBbox) {
                const features = [];
                for await (const feature of flatgeobufGeojson.deserialize(url, requestedBbox)) features.push(feature);
                return { type: 'FeatureCollection', features };
            }
            const resp = await fetch(url);
            const contentType = resp.headers.get('content-type');
            if (contentType && contentType.includes('text/html')) {
                throw new Error(`Expected FlatGeobuf but received HTML for ${url}`);
            }

            const geojsonData = await load(resp, FlatGeobufLoader);
            return asFeatureCollection(geojsonData);
        }

        // --- Fallback for standard JSON files ---
        const resp = await fetch(url);
        return await resp.json();
    } catch (err) {
        // Graceful fallback: if an opt-in Pacific-wide tile has not been
        // generated yet (missing .fgb and .json), show the Palau-box tile for
        // that same dial/date instead of leaving the map blank.
        const pacificPattern = /\/CRW-BAA-Pacific\/[^/]+\//;
        if (pacificPattern.test(url)) {
            const fallbackUrl = url.replace(pacificPattern, '/CRW-BAA/');
            console.warn(`getJSON: Pacific tile unavailable, falling back to ${fallbackUrl}`);
            return getJSON(fallbackUrl);
        }
        // Same idea for the opt-in whole-Pacific Marine Heat Wave layer: fall back
        // to the Palau-box (unmasked) tile for that same timescale/maptype/date.
        // /mhw-Pacific/{res}/{timescale}_{maptype}/... -> /mhw/mhw_{timescale}_{maptype}/...
        const mhwPacPattern = /\/mhw-Pacific\/[^/]+\/([^/]+)\//;
        const mhwMatch = url.match(mhwPacPattern);
        if (mhwMatch) {
            const fallbackUrl = url.replace(mhwPacPattern, `/mhw/mhw_${mhwMatch[1]}/`);
            console.warn(`getJSON: Pacific MHW tile unavailable, falling back to ${fallbackUrl}`);
            return getJSON(fallbackUrl);
        }
        console.error(`getJSON: error obtaining ${url}`, err);
        throw err;
    }
}
